import pino from "pino";

/**
 * Logger configuration options
 */
export interface LoggerConfig {
  level?: "trace" | "debug" | "info" | "warn" | "error" | "fatal";
  enabled?: boolean;
  name?: string;
  prettyPrint?: boolean;
  redact?: string[];
}

/**
 * Logger interface for consistent logging across data layer adapters
 */
export interface Logger {
  trace: (message: string, ...args: any[]) => void;
  debug: (message: string, ...args: any[]) => void;
  info: (message: string, ...args: any[]) => void;
  warn: (message: string, ...args: any[]) => void;
  error: (message: string, error?: Error, ...args: any[]) => void;
  fatal: (message: string, error?: Error, ...args: any[]) => void;
  child: (bindings: Record<string, any>) => Logger;
}

/**
 * No-op logger for when logging is disabled
 */
class NoOpLogger implements Logger {
  trace() {}
  debug() {}
  info() {}
  warn() {}
  error() {}
  fatal() {}
  child(): Logger {
    return new NoOpLogger();
  }
}

/**
 * Pino logger wrapper
 */
class PinoLogger implements Logger {
  private logger: pino.Logger;

  constructor(logger: pino.Logger) {
    this.logger = logger;
  }

  trace(message: string, ...args: any[]): void {
    this.logger.trace({ args }, message);
  }

  debug(message: string, ...args: any[]): void {
    this.logger.debug({ args }, message);
  }

  info(message: string, ...args: any[]): void {
    this.logger.info({ args }, message);
  }

  warn(message: string, ...args: any[]): void {
    this.logger.warn({ args }, message);
  }

  error(message: string, error?: Error, ...args: any[]): void {
    this.logger.error({ error, args }, message);
  }

  fatal(message: string, error?: Error, ...args: any[]): void {
    this.logger.fatal({ error, args }, message);
  }

  child(bindings: Record<string, any>): Logger {
    return new PinoLogger(this.logger.child(bindings));
  }
}

/**
 * Create a logger instance
 */
export function createLogger(config: LoggerConfig = {}): Logger {
  const {
    level = "info",
    enabled = true,
    name = "@shopkit/data-layer",
    prettyPrint = false,
    redact = ["password", "token", "apiKey", "secret"],
  } = config;

  // Return no-op logger if disabled
  if (!enabled) {
    return new NoOpLogger();
  }

  // Browser-friendly configuration
  const pinoConfig: pino.LoggerOptions = {
    name,
    level,
    redact,
    browser: {
      asObject: true,
      serialize: true,
      write: {
        trace: (o: any) => console.trace(formatBrowserLog(o)),
        debug: (o: any) => console.debug(formatBrowserLog(o)),
        info: (o: any) => console.info(formatBrowserLog(o)),
        warn: (o: any) => console.warn(formatBrowserLog(o)),
        error: (o: any) => console.error(formatBrowserLog(o)),
        fatal: (o: any) => console.error(formatBrowserLog(o)),
      },
    },
  };

  // Add pretty printing for development
  if (prettyPrint && typeof window === "undefined") {
    pinoConfig.transport = {
      target: "pino-pretty",
      options: {
        colorize: true,
        translateTime: "HH:MM:ss Z",
        ignore: "pid,hostname",
      },
    };
  }

  const pinoLogger = pino(pinoConfig);
  return new PinoLogger(pinoLogger);
}

/**
 * Format log messages for browser console
 */
export function formatBrowserLog(logObj: any): string {
  const { level, time, msg, name, args, error, ...rest } = logObj;

  let message = `[${name}] ${msg}`;

  if (args && args.length > 0) {
    message += ` ${args
      .map((arg: any) =>
        typeof arg === "object" ? JSON.stringify(arg) : String(arg)
      )
      .join(" ")}`;
  }

  if (error) {
    message += ` Error: ${error.message}`;
    if (error.stack) {
      message += `\n${error.stack}`;
    }
  }

  if (Object.keys(rest).length > 0) {
    message += ` ${JSON.stringify(rest)}`;
  }

  return message;
}

/**
 * Default logger instance for data layer
 */
export const logger = createLogger({
  enabled: process.env.NODE_ENV !== "production",
  level: (process.env.LOG_LEVEL as any) || "info",
  prettyPrint: process.env.NODE_ENV === "development",
});

/**
 * Create adapter-specific logger
 */
export function createAdapterLogger(
  adapterName: string,
  config: LoggerConfig = {}
): Logger {
  const baseLogger = createLogger(config);
  return baseLogger.child({ adapter: adapterName });
}

/**
 * Create service-specific logger
 */
export function createServiceLogger(
  serviceName: string,
  config: LoggerConfig = {}
): Logger {
  const baseLogger = createLogger(config);
  return baseLogger.child({ service: serviceName });
}

/**
 * Create domain-specific logger (e.g., product, cart, customer)
 */
export function createDomainLogger(
  domain: string,
  adapter: string,
  config: LoggerConfig = {}
): Logger {
  const baseLogger = createLogger(config);
  return baseLogger.child({ domain, adapter });
}
